import { useEffect, useState } from "react";

import { SplashScreen } from "@/components/SplashScreen";
import { useAuthContext } from "@/hooks/use-auth-context";
import { useOrganization } from "@/hooks/use-organization";
import { useRouter } from "@/routes/hooks";

type Props = {
  children: React.ReactNode;
};

export function OrganizationAccessGuard({ children }: Props) {
  const router = useRouter();
  const { user, loading: authLoading } = useAuthContext();
  const { organizations, loading } = useOrganization();
  const [isChecking, setIsChecking] = useState(true);
  const provisionedOrganizationId =
    typeof user?.user_metadata?.provisionedOrganizationId === "string"
      ? user.user_metadata.provisionedOrganizationId
      : "";
  const needsOnboarding =
    user?.user_metadata?.needsOrganizationOnboarding === true ||
    user?.user_metadata?.needsAccountSetup === true;
  const hasStaleOnboardingMetadata =
    needsOnboarding &&
    organizations.length > 0 &&
    (!provisionedOrganizationId || !organizations.some((organization) => organization.id === provisionedOrganizationId));

  useEffect(() => {
    if (loading || authLoading) {
      return;
    }

    if (needsOnboarding && !hasStaleOnboardingMetadata) {
      router.replace("/onboarding");
      return;
    }

    if (organizations.length === 0) {
      router.replace(needsOnboarding ? "/onboarding" : "/auth/post-auth");
      return;
    }

    setIsChecking(false);
  }, [
    authLoading,
    hasStaleOnboardingMetadata,
    loading,
    needsOnboarding,
    organizations.length,
    provisionedOrganizationId,
    router,
  ]);

  if (isChecking || loading || authLoading) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
